<template>
  <div class="full-container">
    <div class="search-bar">
      <div class="flex-wrap">
        <Input
          v-model="searchText"
          placeholder="搜索技能名称或描述"
          clearable
          style="width: 280px; margin-right: 10px;"
          @on-enter="handleSearch"
        />
        <Button type="primary" icon="md-search" @click="handleSearch" style="margin-right: 10px;">搜索</Button>
        <Button type="success" icon="md-add" @click="handleAdd">上传技能</Button>
      </div>
    </div>

    <div class="table-wrap">
      <BaseTable
        ref="table"
        full
        :columns="columns"
        :data="filteredData"
        :loading="loading"
        border
        @event="handleTableEvent"
      />
    </div>

    <Modal
      v-model="modalVisible"
      :title="editId ? '修改技能' : '上传技能'"
      width="520"
      :loading="confirmLoading"
      @on-ok="handleSubmit"
      @on-cancel="modalVisible = false"
    >
      <Form ref="formRef" :model="formData" :rules="formRules" :label-width="100">
        <FormItem label="技能名称" prop="name">
          <Input v-model="formData.name" placeholder="请输入技能名称" maxlength="64" />
        </FormItem>
        <FormItem label="描述" prop="description">
          <Input
            v-model="formData.description"
            type="textarea"
            :rows="3"
            placeholder="可选，简要说明技能用途"
          />
        </FormItem>
        <FormItem label="版本号" prop="versionStr">
          <Input v-model="formData.versionStr" placeholder="格式如 1.0.0" style="width: 160px;" />
        </FormItem>
        <FormItem label="技能文件" prop="file">
          <div class="file-upload">
            <input
              ref="fileInput"
              type="file"
              class="file-input-hidden"
              accept=".zip,.rar,.skill"
              @change="onFileChange"
            />
            <Button icon="md-cloud-upload" :loading="uploading" @click="triggerFileSelect">
              {{ formData.file ? '重新选择' : '选择文件' }}
            </Button>
            <span v-if="formData.file" class="file-name">
              {{ fileName || '已上传' }}
              <span v-if="formData.size" class="file-size">（{{ formData.size }} KB）</span>
            </span>
            <div class="file-tip">仅支持 .zip、.rar 或 .skill 文件包</div>
            <div v-if="uploading" class="file-progress">上传中 {{ uploadPercent }}%</div>
          </div>
        </FormItem>
      </Form>
    </Modal>
  </div>
</template>

<script>
import moment from 'moment';

const API_BASE = '/ai/skills';
const ALLOWED_EXTS = ['zip', 'rar', 'skill'];
const STATUS_COLOR = {
  待审核: 'default',
  安全: 'success',
  警告: 'warning',
};

/** a.b.c → a*256*256 + b*256 + c */
function encodeVersion(str) {
  const parts = String(str || '').trim().split('.');
  if (parts.length !== 3) return null;
  const a = parseInt(parts[0], 10);
  const b = parseInt(parts[1], 10);
  const c = parseInt(parts[2], 10);
  if ([a, b, c].some((n) => Number.isNaN(n) || n < 0 || n > 255)) return null;
  return a * 256 * 256 + b * 256 + c;
}

/** number → a.b.c */
function decodeVersion(n) {
  if (n == null || n === '') return '-';
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  const a = Math.floor(num / (256 * 256));
  const b = Math.floor((num % (256 * 256)) / 256);
  const c = num % 256;
  return `${a}.${b}.${c}`;
}

function getExt(name) {
  if (!name) return '';
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}

export default {
  data() {
    return {
      loading: false,
      items: [],
      searchText: '',

      modalVisible: false,
      confirmLoading: false,
      editId: null,
      uploading: false,
      uploadPercent: 0,
      fileName: '',
      formData: {
        name: '',
        description: '',
        versionStr: '1.0.0',
        file: '',
        size: 0,
      },
      formRules: {
        name: [{ required: true, message: '请输入技能名称', trigger: 'blur' }],
        versionStr: [
          {
            required: true,
            validator: (rule, value, callback) => {
              if (!value || encodeVersion(value) == null) {
                callback(new Error('请填写正确的版本号，格式 a.b.c（每段 0-255）'));
              } else {
                callback();
              }
            },
            trigger: 'blur',
          },
        ],
        file: [{ required: true, message: '请上传技能文件包', trigger: 'change' }],
      },

      columns: [
        { type: 'index', width: 60, title: '序号' },
        {
          title: '技能名称',
          key: 'name',
          minWidth: 160,
          type: 'text',
        },
        {
          title: '描述',
          key: 'description',
          minWidth: 180,
          ellipsis: true,
          render: (h, params) => h('span', params.row.description || '-'),
        },
        {
          title: '版本',
          key: 'version',
          width: 100,
          align: 'center',
          render: (h, params) => h('span', decodeVersion(params.row.version)),
        },
        {
          title: '大小',
          key: 'size',
          width: 100,
          align: 'right',
          render: (h, params) => {
            const size = params.row.size;
            if (size == null || size === '') return h('span', '-');
            return h('span', `${size} KB`);
          },
        },
        {
          title: '状态',
          key: 'status',
          width: 100,
          align: 'center',
          render: (h, params) => {
            const status = params.row.status || '待审核';
            return h('Tag', { props: { color: STATUS_COLOR[status] || 'default' } }, status);
          },
        },
        {
          title: '上传时间',
          key: 'created_at',
          width: 160,
          render: (h, params) => {
            const t = params.row.created_at;
            return h('span', t ? moment(t).format('YYYY-MM-DD HH:mm') : '-');
          },
        },
        {
          title: '上传人',
          type: 'user',
          key: 'created_by',
          width: 120,
        },
        {
          title: '操作',
          key: 'action',
          fixed: 'right',
          width: 180,
          render: (h, params) => {
            const row = params.row;
            const btns = [
              h(
                'Button',
                {
                  props: { size: 'small', type: 'primary', ghost: true, disabled: !row.file },
                  style: { marginRight: '8px' },
                  on: { click: () => this.handleDownload(row) },
                },
                '下载'
              ),
            ];
            if (this.canEditOrDelete(row)) {
              btns.push(
                h(
                  'Button',
                  {
                    props: { size: 'small' },
                    style: { marginRight: '8px' },
                    on: { click: () => this.openEdit(row) },
                  },
                  '修改'
                ),
                h(
                  'Button',
                  {
                    props: { size: 'small' },
                    on: { click: () => this.handleDelete(row) },
                  },
                  '删除'
                )
              );
            }
            return h('div', btns);
          },
        },
      ],
    };
  },
  computed: {
    session() {
      return this.$store.getters['session/session'] || {};
    },
    filteredData() {
      let result = [...this.items];
      if (this.searchText) {
        const q = this.searchText.toLowerCase();
        result = result.filter(
          (item) =>
            (item.name && item.name.toLowerCase().includes(q)) ||
            (item.description && (item.description + '').toLowerCase().includes(q))
        );
      }
      return result;
    },
  },
  mounted() {
    this.$api.afterLogin(() => {
      this.fetchData();
    });
  },
  methods: {
    canEditOrDelete(row) {
      if (!this.session || !this.session.id) return false;
      return this.session.id === row.created_by || this.session.is_admin === true;
    },
    handleSearch() {},
    async fetchData() {
      this.loading = true;
      try {
        const res = await this.$api.get(API_BASE);
        this.items = (res.data && res.data.data) || res.data || [];
        if (!Array.isArray(this.items)) this.items = [];
      } catch (err) {
        this.$Message.error('获取技能列表失败');
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.formData = {
        name: '',
        description: '',
        versionStr: '1.0.0',
        file: '',
        size: 0,
      };
      this.fileName = '';
      this.uploadPercent = 0;
      this.uploading = false;
    },
    handleAdd() {
      this.editId = null;
      this.resetForm();
      this.modalVisible = true;
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.resetFields());
    },
    openEdit(row) {
      this.editId = row.id;
      this.formData = {
        name: row.name || '',
        description: row.description || '',
        versionStr: decodeVersion(row.version) === '-' ? '1.0.0' : decodeVersion(row.version),
        file: row.file || '',
        size: row.size || 0,
      };
      this.fileName = this.guessFileName(row.file);
      this.modalVisible = true;
    },
    guessFileName(url) {
      if (!url) return '';
      try {
        const path = String(url).split('?')[0];
        return decodeURIComponent(path.substring(path.lastIndexOf('/') + 1)) || '';
      } catch (e) {
        return '';
      }
    },
    handleTableEvent(e) {
      const { type, data: row } = e || {};
      if (type === 'delete') this.handleDelete(row);
    },
    triggerFileSelect() {
      if (this.uploading) return;
      this.$refs.fileInput && this.$refs.fileInput.click();
    },
    onFileChange(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = '';
      if (!file) return;
      const ext = getExt(file.name);
      if (!ALLOWED_EXTS.includes(ext)) {
        this.$Message.warning('仅支持 .zip、.rar 或 .skill 文件包');
        return;
      }
      this.uploadFile(file);
    },
    uploadFile(file) {
      this.uploading = true;
      this.uploadPercent = 0;
      this.fileName = file.name;
      const sizeKb = Math.max(1, Math.ceil(file.size / 1024));
      this.$cos.upload(
        [file],
        {
          onFail: () => {
            this.uploading = false;
            this.$Message.error('文件上传失败');
          },
          onProgress: (o) => {
            this.uploadPercent = Math.round((o && o.percent) || 0);
          },
          onSuccess: (files) => {
            this.uploading = false;
            if (!Array.isArray(files) || !files[0] || !files[0].url) {
              this.$Message.error('文件上传失败');
              return;
            }
            this.formData.file = files[0].url;
            this.formData.size = sizeKb;
            this.$refs.formRef && this.$refs.formRef.validateField('file');
            this.$Message.success('文件上传成功');
          },
        },
        { coskey: this.session.coskey }
      );
    },
    handleDownload(row) {
      if (!row || !row.file) {
        this.$Message.info('暂无可下载文件');
        return;
      }
      const name = (row.name || 'skill').replace(/[^\w\u4e00-\u9fa5.-]/g, '_');
      const ext = getExt(row.file) || getExt(this.guessFileName(row.file)) || 'zip';
      this.DownloadWithName(row.file, `${name}.${ext}`);
    },
    async handleSubmit() {
      try {
        const valid = await this.$refs.formRef.validate();
        if (!valid) return;
        if (this.uploading) {
          this.$Message.warning('文件正在上传，请稍候');
          return;
        }
        if (!this.formData.file) {
          this.$Message.warning('请上传技能文件包');
          return;
        }
        const version = encodeVersion(this.formData.versionStr);
        if (version == null) {
          this.$Message.warning('版本号格式不正确');
          return;
        }
        this.confirmLoading = true;
        const payload = {
          name: this.formData.name,
          description: this.formData.description || '',
          file: this.formData.file,
          size: this.formData.size || 0,
          version,
        };
        if (this.editId) {
          await this.$api.patch(`${API_BASE}/${this.editId}`, payload);
          this.$Message.success('修改成功');
        } else {
          payload.status = '待审核';
          await this.$api.post(API_BASE, payload);
          this.$Message.success('上传成功');
        }
        this.modalVisible = false;
        this.fetchData();
      } catch (err) {
        if (err && err.message) this.$Message.error(err.message);
      } finally {
        this.confirmLoading = false;
      }
    },
    handleDelete(row) {
      this.$Modal.confirm({
        title: '确认删除',
        content: `确定要删除技能「${row.name}」吗？`,
        onOk: async () => {
          try {
            await this.$api.delete(`${API_BASE}/${row.id}`);
            this.$Message.success('删除成功');
            this.fetchData();
          } catch (err) {
            this.$Message.error((err && err.message) || '删除失败');
          }
        },
      });
    },
  },
};
</script>

<style scoped>
.search-bar {
  padding: 15px;
  border-bottom: 1px solid var(--border);
}
.search-bar .flex-wrap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.full-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}
.table-wrap {
  flex: 1;
  min-height: 0;
  padding: 15px;
  overflow: auto;
}
.table-wrap .gz-table {
  height: 100%;
}
.file-upload {
  line-height: 1.6;
}
.file-input-hidden {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  overflow: hidden;
}
.file-name {
  margin-left: 10px;
  color: var(--text2);
  word-break: break-all;
}
.file-size {
  color: var(--subtext2);
}
.file-tip {
  margin-top: 6px;
  font-size: 12px;
  color: var(--subtext2);
}
.file-progress {
  margin-top: 4px;
  font-size: 12px;
  color: var(--primary);
}
</style>
